import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Gentelella's button scale, by way of the Megaconnect panel.
 *
 * Heights are one step down (h-9, not h-10) because the base font is 14px: at
 * that size an h-10 control reads as oversized beside the text next to it.
 *
 * Filled variants use `--primary-strong`. It holds the same value as
 * `--primary` under this monochrome brand; the name is kept because the ROLE
 * split is real -- identity colour versus a fill that has text on it.
 *
 * `destructive` is the one that needs the split for a measured reason: white on
 * the app red #ef4444 is 3.76:1, under the 4.5 AA floor, so the filled variant
 * uses `--destructive-strong` while icons and badges keep the plain red.
 *
 * DISABLED IS A FLAT FILL, NOT `opacity-50`. Half-opacity took a filled
 * button's white label to 1.47:1 -- unreadable, on exactly the actions that
 * stay disabled longest because they are the ones with a precondition. Every
 * variant lands on the same bordered grey, so "off" looks like one thing rather
 * than six faded things. `disabled:border` is spelled out as well as
 * `disabled:border-border`: Tailwind's colour utility sets the COLOUR only, and
 * preflight leaves the width at 0, so on the variants with no border of their
 * own the rule painted nothing at all.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded text-sm font-medium cursor-pointer transition active:scale-[0.98] disabled:pointer-events-none disabled:border disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
  {
    variants: {
      variant: {
        // An explicit hover colour, not hover:opacity-90 -- opacity washes a
        // filled control out against a tinted page instead of darkening it.
        default: "bg-primary-strong text-primary-foreground hover:bg-primary-strong-hover",
        outline: "border border-border bg-card text-foreground hover:bg-hover hover:border-secondary/40",
        // Bordered, like `outline`. The fill alone is --muted on --card, which
        // measures 1.05:1: on a white card it had no visible edge and read as a
        // label rather than a control.
        secondary: "border border-border bg-muted text-foreground hover:bg-hover",
        destructive: "bg-destructive-strong text-destructive-foreground hover:brightness-95",
        ghost: "text-foreground hover:bg-hover",
        link: "text-primary-strong dark:text-primary underline-offset-4 hover:underline",
      },
      size: { default: "h-9 px-4", sm: "h-8 px-3", icon: "h-9 w-9" },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    // `type="button"` by default. The HTML default is `submit`, which turns
    // every unlabelled button inside a form into one that submits it -- and
    // this panel puts Approve and Delete inside forms.
    <button ref={ref} type={type} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  ),
);
Button.displayName = "Button";
